import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../LanguageContext";
import Markdown from "./Markdown";

const ORANGE = "#c4622d";

function Message({ msg }) {
  const { t } = useTranslation();
  const [sqlOpen, setSqlOpen] = useState(false);

  if (msg.role === "user") {
    return (
      <div style={styles.userBubble}>
        <span>{msg.content}</span>
      </div>
    );
  }

  if (msg.error) {
    return (
      <div style={styles.errorBubble}>
        <strong>{t.error}:</strong> {msg.content}
      </div>
    );
  }

  return (
    <div style={styles.assistantBubble}>
      <Markdown>{msg.content}</Markdown>
      {msg.sql && (
        <div>
          <button style={styles.sqlToggle} onClick={() => setSqlOpen(!sqlOpen)}>
            {sqlOpen ? t.hideSQL : t.showSQL}
          </button>
          {sqlOpen && <pre style={styles.sqlBlock}>{msg.sql}</pre>}
        </div>
      )}
    </div>
  );
}

// Approximate the backend pipeline stages so the user sees something
// changing while a query is in flight. The backend doesn't stream, so we
// advance based on elapsed time; stages stall on the last one if the
// request takes longer than expected.
function ProgressIndicator() {
  const { t } = useTranslation();
  const [stageIdx, setStageIdx] = useState(0);
  const stages = [
    t.progressGenerating,
    t.progressRunning,
    t.progressAnalyzing,
    t.progressFinalizing,
  ];

  useEffect(() => {
    // Cumulative ms at which to advance to each subsequent stage.
    const stageTimings = [1800, 3800, 6500];
    const timers = stageTimings.map((ms, i) =>
      setTimeout(() => setStageIdx(i + 1), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div style={styles.assistantBubble}>
      <span style={styles.typing}>
        <span style={styles.spinner} aria-hidden="true" />
        {stages[Math.min(stageIdx, stages.length - 1)]}
      </span>
    </div>
  );
}

function buildWelcome(t, stats) {
  if (!stats) return t.welcomeNoStats;
  const races = stats.events?.scraped ?? stats.events?.total ?? 0;
  const results = stats.results_total ?? 0;
  return t.welcomeStats(races.toLocaleString(), results.toLocaleString());
}

export default function ChatPanel({ onResult, loading, setLoading, result }) {
  const { t, lang } = useTranslation();
  const [input, setInput] = useState("");
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState(null);
  const [shareState, setShareState] = useState(null); // null | 'pending' | 'done' | 'error'
  const bottomRef = useRef(null);

  const [shareUrl, setShareUrl] = useState(null);

  const share = async () => {
    if (shareState === "pending") return;
    setShareState("pending");
    setShareUrl(null);
    try {
      const { createShare } = await import("../api");
      const data = await createShare(messages, result);
      const url = `${window.location.origin}/share/${data.id}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareState("done");
      } catch {
        // Clipboard blocked — show the URL inline instead
        setShareUrl(url);
        setShareState("done");
      }
    } catch {
      setShareState("error");
    } finally {
      setTimeout(() => setShareState(null), 2500);
    }
  };

  useEffect(() => {
    import("../api").then(({ getStats }) =>
      getStats().then((data) => {
        setStats(data);
        setMessages([{ role: "assistant", content: buildWelcome(t, data), isWelcome: true }]);
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update welcome message when language changes
  useEffect(() => {
    setMessages((prev) => {
      if (!prev || prev.length === 0) return prev;
      if (!prev[0].isWelcome) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], content: buildWelcome(t, stats) };
      return updated;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, stats]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const buildHistory = (msgs) =>
    msgs
      .filter((m) => !m.error && m.role !== undefined)
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));

  const submit = async () => {
    const q = input.trim();
    if (!q || loading || !messages) return;

    const userMsg = { role: "user", content: q };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const { askQuestion } = await import("../api");
      const data = await askQuestion(q, buildHistory(messages));
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.explanation, sql: data.sql },
      ]);
      onResult({ columns: data.columns, rows: data.rows, chart: data.chart ?? null });
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: err.message, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>{t.assistant}</h2>
        {messages && messages.some((m) => m.role === "user") && (
          <div style={styles.shareArea}>
            <button
              style={{
                ...styles.shareBtn,
                ...(shareState === "done" ? styles.shareBtnDone : {}),
                ...(shareState === "error" ? styles.shareBtnError : {}),
              }}
              onClick={share}
              disabled={shareState === "pending"}
            >
              {shareState === "done"
                ? shareUrl ? "✓ Link ready" : "✓ Link copied!"
                : shareState === "error"
                ? "Failed — retry?"
                : shareState === "pending"
                ? "Saving…"
                : "Share"}
            </button>
            {shareUrl && (
              <input
                style={styles.shareUrlInput}
                value={shareUrl}
                readOnly
                onClick={(e) => e.target.select()}
              />
            )}
          </div>
        )}
      </div>

      <div style={styles.messages}>
        {(messages ?? []).map((msg, i) => (
          <Message key={i} msg={msg} />
        ))}
        {loading && <ProgressIndicator />}
        <div ref={bottomRef} />
      </div>

      <div style={styles.inputRow}>
        <textarea
          style={styles.textarea}
          placeholder={t.placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={2}
          disabled={loading}
        />
        <button
          style={{
            ...styles.sendBtn,
            background: loading || !input.trim() ? "#ddd" : ORANGE,
            cursor: loading || !input.trim() ? "default" : "pointer",
          }}
          onClick={submit}
          disabled={loading || !input.trim()}
        >
          {t.ask}
        </button>
      </div>
    </div>
  );
}

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    borderRight: "1px solid #e1e1e1",
  },
  header: {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #e1e1e1",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  shareBtn: {
    marginLeft: "auto",
    background: "none",
    border: "1px solid #c4622d",
    borderRadius: "6px",
    color: "#c4622d",
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: 600,
    padding: "3px 12px",
    transition: "background 0.15s, color 0.15s",
  },
  shareBtnDone: {
    background: "#e6f4ea",
    borderColor: "#34a853",
    color: "#34a853",
  },
  shareBtnError: {
    background: "#fce8e6",
    borderColor: "#d93025",
    color: "#d93025",
  },
  shareArea: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  shareUrlInput: {
    fontSize: "0.75rem",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "3px 8px",
    color: "#555",
    width: "200px",
    cursor: "text",
  },
  title: {
    margin: 0,
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#c4622d",
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  userBubble: {
    alignSelf: "flex-end",
    background: "#c4622d",
    color: "#fff",
    borderRadius: "18px 18px 4px 18px",
    padding: "10px 14px",
    maxWidth: "80%",
    fontSize: "0.9rem",
  },
  assistantBubble: {
    alignSelf: "flex-start",
    background: "#f4f5f6",
    borderRadius: "18px 18px 18px 4px",
    padding: "10px 14px",
    maxWidth: "90%",
    fontSize: "0.9rem",
    color: "#333",
  },
  errorBubble: {
    alignSelf: "flex-start",
    background: "#fff0f0",
    border: "1px solid #ffcccc",
    borderRadius: "8px",
    padding: "10px 14px",
    maxWidth: "90%",
    fontSize: "0.85rem",
    color: "#c00",
  },
  typing: {
    color: "#999",
    fontStyle: "italic",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
  spinner: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    border: "2px solid #ddd",
    borderTopColor: ORANGE,
    animation: "nyrr-spin 0.8s linear infinite",
  },
  sqlToggle: {
    background: "none",
    border: "none",
    color: "#c4622d",
    cursor: "pointer",
    fontSize: "0.8rem",
    padding: 0,
  },
  sqlBlock: {
    marginTop: "6px",
    padding: "8px",
    background: "#1e1e1e",
    color: "#d4d4d4",
    borderRadius: "4px",
    fontSize: "0.75rem",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
  },
  inputRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "8px",
    padding: "12px 16px",
    borderTop: "1px solid #e1e1e1",
    background: "#fff",
    alignItems: "stretch",
    flexShrink: 0,
  },
  textarea: {
    resize: "none",
    border: "1px solid #d1d1d1",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "1rem",
    fontFamily: "inherit",
    outline: "none",
    WebkitAppearance: "none",
  },
  sendBtn: {
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "0 20px",
    fontWeight: 600,
    fontSize: "0.9rem",
    minWidth: "64px",
    flexShrink: 0,
  },
};
